import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';
import { v4 as uuidv4 } from 'uuid';

import FormFieldsCompact from '../form/fieldsCompact';
import fields from '../../lib/fields';
import { exifGpsToDecimal, normalizeYrEntry } from '../../lib/fishCatch';

// Shared machinery behind BOTH /fangst (fishCatch) and /kontakt (fishContact).
//
// This is a FACTORY returning a configured component class, not a wrapper
// component: the existing enzyme suites call wrapper.instance().buildHoldingStore(),
// .buildSections() and read .state.fishCatch directly, all of which a wrapper
// would hide. For the same reason the working entry lives at
// this.state[entryName] — for a catch that IS `state.fishCatch`, unchanged.
//
// NB: `catch` is a JS reserved word — no identifier here is ever a bare `catch`.

// Free-text fields (as opposed to `species`, which has a fixed built-in
// vocabulary) whose datalist is populated entirely from the user's own previous
// entries via GET /api/articles/fishcatch-values. That endpoint serves catches
// and contacts from one shared pool.
const SUGGESTION_TEXT_FIELDS = ['fly', 'equipment', 'placeName'];

const EMPTY_SUGGESTIONS = {
    fly: [], equipment: [], placeName: [], species: [],
};

export function createFishEntryDrawer({
    entryName, dateField, testIdPrefix, titleId, titleDefault,
}) {
    // The working entry lives in this.state, not a MobX store — it is transient
    // until onSave fires and the parent decides what to do with it.
    class FishEntryDrawer extends Component {
        constructor(props) {
            super(props);
            // If an entry was passed in (editing an already-started one, e.g.
            // reopening the drawer), use it as-is — the spread preserves real
            // falsy values like released: false. Otherwise start blank.
            this.state = {
                [entryName]: props[entryName] ? { ...props[entryName] } : {
                    uuidv4: uuidv4(),
                    [dateField]: util.isoDate(),
                },
                isSaving: false,
                // Populated (best-effort) by fetchSuggestions on mount. Starts as
                // empty arrays so buildSections behaves identically whether the
                // fetch hasn't returned yet, failed, or succeeded with no data.
                suggestions: EMPTY_SUGGESTIONS,
            };
        }

        // Key of the position/date a reactive weather fetch was last started for.
        // Not observable state on purpose — it only guards against re-firing the
        // fetch on every render for a position we've already requested weather
        // for; it never needs to trigger a render itself.
        weatherFetchKey = null;

        componentDidMount() {
            this.reactToEntryChanges();
            this.fetchSuggestions();
        }

        componentDidUpdate() {
            this.reactToEntryChanges();
        }

        // Minimal store-shaped adapter so FormFieldsCompact/Input can read and write
        // the working entry without a real MobX store behind it. Input.js only ever
        // reaches the persistence branches (saveField/adminUpdate/updateField) when
        // object.id is set — our transient entry never has one, so every edit lands
        // in updateObjectKeyValue, which merges into this.state[entryName].
        buildHoldingStore = () => {
            const entry = this.state[entryName];
            return {
                [entryName]: entry,
                foundList: [],
                foundListKeys: [],
                // input.js destructures `saved` from every holdingStore WITHOUT a
                // default and indexes it on every field render — omitting it
                // crashes the whole form (prod errors 31592/31593).
                saved: {},
                updateKeyValue: (key, value) => {
                    this.setState({ [key]: value });
                },
                updateObjectKeyValue: (objectName, key, value) => {
                    this.setState(prevState => ({
                        [objectName]: { ...prevState[objectName], [key]: value },
                    }));
                },
                updateField: () => {},
                saveField: async () => null,
                adminUpdate: async () => null,
                adminUpdateLocal: () => {},
                // The images field's remove button calls
                // holdingStore.removeImage({ id, md5 }) (input.js). The transient
                // entry has no id, so just drop the image from local state.
                removeImage: ({ md5 }) => {
                    this.setState(prevState => ({
                        [entryName]: {
                            ...prevState[entryName],
                            images: (prevState[entryName].images || []).filter(img => img.md5 !== md5),
                        },
                    }));
                },
            };
        }

        // Best-effort: previously-used fly/equipment/placeName/species values from
        // the logged-in user's own catches AND contacts, used to augment the
        // datalist suggestions in buildSections below. A failed or slow fetch must
        // never block the drawer — state.suggestions simply stays at
        // EMPTY_SUGGESTIONS, same as if the fetch hadn't been made at all.
        fetchSuggestions = async () => {
            try {
                const response = await util.fetchApi('/api/articles/fishcatch-values', { method: 'GET' });
                const data = response && response.data;
                if (!data) {
                    return;
                }
                this.setState({
                    suggestions: {
                        fly: Array.isArray(data.fly) ? data.fly : [],
                        equipment: Array.isArray(data.equipment) ? data.equipment : [],
                        placeName: Array.isArray(data.placeName) ? data.placeName : [],
                        species: Array.isArray(data.species) ? data.species : [],
                    },
                });
            } catch (err) {
                // best-effort only; suggestions stay at EMPTY_SUGGESTIONS
            }
        }

        // Copies fields.get(entryName) and augments it with the suggestions
        // fetched above: fly/equipment/placeName (type: 'datalist', no built-in
        // vocabulary) get their datalist populated directly from the user's past
        // values, while species (which already has the fixed FISH_SPECIES list)
        // gets the user's own previously-used species merged in, skipping values
        // already present. Never mutates the shared section/field/datalist arrays
        // returned by fields.js — every section/field touched here is a new copy.
        buildSections = () => {
            const { suggestions } = this.state;
            const baseSections = fields.get(entryName);
            return baseSections.map(section => ({
                ...section,
                fields: section.fields.map((field) => {
                    if (field.name === 'species' && Array.isArray(field.datalist)) {
                        const known = new Set(field.datalist.map(([value]) => value));
                        const suggested = (suggestions.species || [])
                            .filter(value => !known.has(value))
                            .map(value => [value, value]);
                        if (suggested.length === 0) {
                            return field;
                        }
                        return { ...field, datalist: [...field.datalist, ...suggested] };
                    }
                    if (SUGGESTION_TEXT_FIELDS.includes(field.name)) {
                        const values = suggestions[field.name];
                        if (!Array.isArray(values) || values.length === 0) {
                            return field;
                        }
                        return { ...field, datalist: values.map(value => [value, value]) };
                    }
                    return field;
                }),
            }));
        }

        // Weather is best-effort. Any failure (network, missing store, bad
        // response) resolves to null so the entry can always still be saved.
        fetchWeather = async (latitude, longitude, entryDate) => {
            const { gpsPointStore } = this.props.stores;
            if (!latitude || !longitude || !gpsPointStore) {
                return null;
            }
            try {
                const when = entryDate ? new Date(entryDate).getTime() : Date.now();
                const isRecent = Math.abs(Date.now() - when) < 60 * 60 * 1000;
                // gpsPointStore is absent from some product builds; the early return above covers it.
                const result = isRecent
                    ? await gpsPointStore.getYrWeather({ lat: latitude, lon: longitude }) // store-guarded
                    : await gpsPointStore.getYrWeatherHistorical({ // store-guarded
                        lat: latitude, lon: longitude, timestamp: when,
                    });
                const entries = result?.data || result;
                const first = Array.isArray(entries) ? entries[0] : null;
                const normalized = normalizeYrEntry(first);
                if (!normalized) {
                    return null;
                }
                return { ...normalized, fetchedAt: new Date() };
            } catch (err) {
                return null; // weather is best-effort; never block the save
            }
        }

        // Priority: photo EXIF GPS, then browserLatitude/browserLongitude, then
        // whatever is already on the entry (a previously derived or manually set
        // position) — the early-return guard means an already-set position is
        // never clobbered by a later automatic derivation.
        derivePosition = (entry) => {
            const { browserLatitude, browserLongitude } = this.props;
            if (entry.latitude && entry.longitude) {
                return entry;
            }
            const firstImage = (entry.images || [])[0];
            const fromExif = exifGpsToDecimal(firstImage?.exif?.gps);
            if (fromExif) {
                const next = {
                    ...entry,
                    latitude: fromExif.latitude,
                    longitude: fromExif.longitude,
                    positionSource: 'exif',
                };
                if (firstImage?.exif?.exif?.DateTimeOriginal) {
                    next[dateField] = firstImage.exif.exif.DateTimeOriginal;
                }
                return next;
            }
            if (browserLatitude && browserLongitude) {
                return {
                    ...entry,
                    latitude: browserLatitude,
                    longitude: browserLongitude,
                    positionSource: 'device',
                };
            }
            return entry;
        }

        // Runs after every mount/update so the drawer reacts progressively as the
        // user edits: adding a photo with EXIF GPS (or DateTimeOriginal) derives
        // the position/date into state immediately — the visible form reflects
        // what will actually be saved, instead of only computing it once at Save.
        // Once a position is known (from any source), the weather fetch is kicked
        // off in the background so Save doesn't have to wait on the network.
        reactToEntryChanges = () => {
            const entry = this.state[entryName];
            const hasPosition = !!(entry.latitude && entry.longitude);

            if (!hasPosition) {
                const derived = this.derivePosition(entry);
                // derivePosition returns the same reference when nothing could be
                // derived yet (no exif, no browser coords) — only commit to state
                // when it actually produced something new. The setState below
                // triggers another componentDidUpdate cycle, which is when the
                // weather fetch (below) picks up the now-known position.
                if (derived !== entry) {
                    this.setState({ [entryName]: derived });
                }
                return;
            }

            this.fetchWeatherReactively(entry);
        }

        // Fire-and-forget weather fetch, keyed on lat/lon/date so it only ever
        // runs once per distinct position+date — re-renders for unrelated state
        // changes (isSaving toggling, other field edits) must not spam the yr.no
        // endpoint. A resolved weather (or an already-set one) also stops it.
        fetchWeatherReactively = (entry) => {
            const { latitude, longitude } = entry;
            const entryDate = entry[dateField];
            const key = `${latitude}|${longitude}|${entryDate || ''}`;
            if (entry.weather || this.weatherFetchKey === key) {
                return;
            }
            this.weatherFetchKey = key;
            this.fetchWeather(latitude, longitude, entryDate).then((weather) => {
                if (!weather) {
                    return;
                }
                this.setState((prevState) => {
                    const current = prevState[entryName];
                    const currentKey = `${current.latitude}|${current.longitude}|${current[dateField] || ''}`;
                    // Only apply if the working entry still matches the position
                    // this fetch was started for — avoids clobbering a newer
                    // position/weather that arrived while this one was in flight.
                    if (currentKey !== key || current.weather) {
                        return null;
                    }
                    return { [entryName]: { ...current, weather } };
                });
            });
        }

        saveEntry = async () => {
            const { onSave, drawerLevel = 1 } = this.props;
            const { appState } = this.props.stores;
            this.setState({ isSaving: true });

            let entry = this.derivePosition(this.state[entryName]);

            // Fallback path: covers an entry saved before the reactive effect above
            // has had a chance to run (e.g. browser coordinates arriving late, or
            // no image at all). If the reactive fetch already resolved weather
            // onto state, reuse it instead of blocking Save on another round-trip.
            if (entry.latitude && entry.longitude && !entry.weather) {
                const weather = await this.fetchWeather(
                    entry.latitude, entry.longitude, entry[dateField],
                );
                if (weather) {
                    entry = { ...entry, weather };
                }
            }

            this.setState({ [entryName]: entry, isSaving: false });
            onSave(entry);
            if (appState?.toggleDrawer) {
                appState.toggleDrawer(false, drawerLevel);
            }
        }

        cancelEntry = () => {
            const { onCancel, drawerLevel = 1 } = this.props;
            const { appState } = this.props.stores;
            onCancel();
            if (appState?.toggleDrawer) {
                appState.toggleDrawer(false, drawerLevel);
            }
        }

        render() {
            const { stores, drawerLevel = 1 } = this.props;
            const { isSaving } = this.state;
            const entry = this.state[entryName];

            const sections = this.buildSections();
            const holdingStore = this.buildHoldingStore();
            const preview = this.derivePosition(entry);
            const positionSource = preview.positionSource || entry.positionSource;

            return (
                <div class={`${testIdPrefix}-drawer`}>
                    <div class='w-100 d-flex justify-content-center'>
                        <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                            <div class='font-weight-lighter px-3 box-header mt-0'>
                                <Text id={titleId}>{titleDefault}</Text>
                            </div>

                            <div class='px-3 mt-2 d-flex align-items-center gap-2'>
                                <span class='badge text-bg-secondary rounded-pill'>
                                    <i class='fa-duotone fa-location-dot me-1' />
                                    {positionSource === 'exif' && (
                                        <Text id={`${entryName}.position-from-photo`}>Position from photo</Text>
                                    )}
                                    {positionSource === 'device' && (
                                        <Text id={`${entryName}.position-from-device`}>Position from device</Text>
                                    )}
                                    {!positionSource && (
                                        <Text id={`${entryName}.no-position`}>No position</Text>
                                    )}
                                </span>
                                {isSaving && (
                                    <span class='text-body-secondary small'>
                                        <i class='fa-solid fa-spinner fa-spin me-1' />
                                        <Text id={`${entryName}.fetching-weather`}>Fetching weather…</Text>
                                    </span>
                                )}
                            </div>

                            <div class='px-3 mt-2'>
                                <div class='card'>
                                    <div class='card-body'>
                                        <FormFieldsCompact
                                            stores={stores}
                                            sections={sections}
                                            holdingStore={holdingStore}
                                            objectName={entryName}
                                            isNew
                                            drawerLevel={drawerLevel}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class='w-100 d-flex justify-content-end mt-3 px-4 py-3 bg-body-secondary fixed-bottom'
                        style='bottom: 0; left: 0;'
                    >
                        <button
                            type='button'
                            class='btn btn-outline-secondary rounded-pill me-2'
                            data-testid={`${testIdPrefix}-cancel`}
                            onClick={this.cancelEntry}
                        >
                            <i class='fa-solid fa-xmark me-1' />
                            <Text id={`${entryName}.cancel`}>Cancel</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-primary rounded-pill'
                            data-testid={`${testIdPrefix}-save`}
                            disabled={isSaving}
                            onClick={this.saveEntry}
                        >
                            <i class='fa-solid fa-check me-1' />
                            <Text id={`${entryName}.save`}>Save</Text>
                        </button>
                    </div>
                </div>
            );
        }
    }

    // observer() patches the class in place and returns it, so instance methods
    // stay reachable via enzyme's wrapper.instance() — the existing suites depend
    // on that. (`@observer class X` is exactly `X = observer(X)`.)
    return observer(FishEntryDrawer);
}

export default createFishEntryDrawer;
